import { useEffect, useState } from "react"
import { IoArrowBack, IoEyeOutline, IoEyeOffOutline } from "react-icons/io5"
import { useNavigate, useLocation } from "react-router-dom"
import PageLayout from "../../components/layouts/PageLayout"
import CustomNavbar from "../../components/common/CustomNavbar"
import { toast } from "sonner"
import { AppDispatch } from "../../store"
import { useDispatch } from "react-redux"
import { addDBConnection, updateDBConnection } from "../../features/database/dbConnectionServices"
import { DbConnection } from "../../types/databaseTypes"

const DB_TYPES = ["MYSQL", "POSTGRES"] as const
type DbType = typeof DB_TYPES[number]

export function AddDB() {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const location = useLocation()

    const editData: DbConnection | undefined = location.state?.editData
    const isEdit = Boolean(editData)

    const [dbType, setDbType] = useState<DbType | "">(
        DB_TYPES.includes(editData?.dbType as DbType) ? (editData!.dbType as DbType) : ""
    )
    const [host, setHost] = useState(editData?.host ?? "")
    const [port, setPort] = useState(editData?.port ? String(editData.port) : "")
    const [databaseName, setDatabaseName] = useState(editData?.databaseName ?? "")
    const [username, setUsername] = useState(editData?.username ?? "")
    const [password, setPassword] = useState(editData?.password ?? "")
    const [showPassword, setShowPassword] = useState(false)
    const [loading, setLoading] = useState(false)

    const validate = () => {
        if (!dbType) { toast.error("Please select a database type"); return false }
        if (!host.trim()) { toast.error("Host is required"); return false }
        if (!port.trim()) { toast.error("Port is required"); return false }
        if (!databaseName.trim()) { toast.error("Database name is required"); return false }
        if (/\s/.test(databaseName)) { toast.error("Database name cannot contain spaces"); return false }
        if (!username.trim()) { toast.error("Username is required"); return false }
        if (/\s/.test(username)) { toast.error("Username cannot contain spaces"); return false }
        return true
    }

    const handleSubmit = async () => {
        if (!validate()) return

        setLoading(true)
        try {
            if (isEdit) {
                await dispatch(
                    updateDBConnection({
                        id: editData!.id,
                        dbType: dbType as DbType,
                        host,
                        port: Number(port),
                        databaseName,
                        username,
                        password,
                    })
                ).unwrap()
                toast.success("Database connection updated successfully")
            } else {
                await dispatch(
                    addDBConnection({
                        dbType: dbType as DbType,
                        host,
                        port: Number(port),
                        databaseName,
                        username,
                        password,
                    })
                ).unwrap()
                toast.success("Database connected successfully")
            }
            navigate(-1)
        } catch (err: any) {
            toast.error(err?.message || err || `Failed to ${isEdit ? "update" : "connect"} database`)
        } finally {
            setLoading(false)
        }
    }

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <IoArrowBack
                            className="text-2xl cursor-pointer hover:text-brand-main transition"
                            onClick={() => navigate(-1)}
                        />
                        <span className="font-bold text-lg uppercase">
                            {isEdit ? "Edit Connection" : "Connect Database"}
                        </span>
                    </div>
                </CustomNavbar>
            }
        >
            <div className="rounded-4xl w-full bg-gray-100">
                <div className="flex flex-col rounded-4xl w-full p-4 bg-white min-h-screen gap-6">

                    {/* Header */}
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">
                            {isEdit ? "Edit Database Connection" : "Connect a Database"}
                        </h2>
                        <p className="text-sm text-text-secondary mt-1">
                            {isEdit
                                ? "Update your connection details below"
                                : "Enter your connection details to link a database"}
                        </p>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 flex flex-col gap-5">

                        {/* DB Type */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                                Database Type *
                            </label>
                            <select
                                value={dbType}
                                onChange={e => setDbType(e.target.value as DbType)}
                                className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white"
                            >
                                <option value="">Select database type</option>
                                {DB_TYPES.map(t => (
                                    <option key={t} value={t}>
                                        {t === "MYSQL" ? "MySQL" : "PostgreSQL"}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Host + Port */}
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex flex-col gap-1 flex-[3]">
                                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                                    Host *
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. localhost or db.example.com"
                                    value={host}
                                    onChange={e => setHost(e.target.value.trim())}
                                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white"
                                />
                            </div>
                            <div className="flex flex-col gap-1 flex-1">
                                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                                    Port *
                                </label>
                                <input
                                    placeholder="port"
                                    value={port}
                                    onChange={e => setPort(e.target.value.trim())}
                                    min={1}
                                    max={65535}
                                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white"
                                />
                            </div>
                        </div>

                        {/* Database Name */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                                Database Name *
                            </label>
                            <input
                                type="text"
                                placeholder="e.g. my_database"
                                value={databaseName}
                                onChange={e => setDatabaseName(e.target.value.replace(/\s/g, "_"))}
                                className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white"
                            />
                        </div>

                        {/* Username + Password */}
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex flex-col gap-1 flex-1">
                                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                                    Username *
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. root"
                                    value={username}
                                    onChange={e => setUsername(e.target.value.replace(/\s/g, ""))}
                                    autoComplete="username"
                                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white"
                                />
                            </div>
                            <div className="flex flex-col gap-1 flex-1">
                                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        autoComplete="current-password"
                                        className="border border-gray-200 rounded-xl px-4 py-2.5 pr-10 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white w-full"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(p => !p)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword
                                            ? <IoEyeOffOutline className="text-lg" />
                                            : <IoEyeOutline className="text-lg" />
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 justify-end pt-1">
                            <button
                                onClick={() => navigate(-1)}
                                className="px-5 py-2.5 rounded-xl border border-gray-200 text-text-secondary font-semibold text-sm hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="px-6 py-2.5 rounded-xl bg-brand-blue-gradient text-white font-bold text-sm disabled:opacity-60 flex items-center gap-2"
                            >
                                {loading
                                    ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {isEdit ? "Updating..." : "Connecting..."}</>
                                    : isEdit ? "Update" : "Connect"
                                }
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </PageLayout>
    )
}